import React, { useEffect, useRef, useState } from 'react';
import './LogoShield.css';

const LogoShield = ({ onClick }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    let time = 0;
    let morphPhase = 0;

    // Data points
    class DataPoint {
      constructor() {
        this.angle = Math.random() * Math.PI * 2;
        this.radius = 25 + Math.random() * 15;
        this.speed = 0.01 + Math.random() * 0.02;
        this.size = 1 + Math.random() * 1;
        this.pulse = Math.random() * Math.PI * 2;
      }

      update() {
        this.angle += this.speed;
        this.pulse += 0.05;
      }

      draw(centerX, centerY) {
        const x = centerX + Math.cos(this.angle) * this.radius;
        const y = centerY + Math.sin(this.angle) * this.radius;
        const opacity = 0.4 + Math.sin(this.pulse) * 0.4;
        const currentSize = this.size + Math.sin(this.pulse) * 0.5;

        ctx.beginPath();
        ctx.arc(x, y, currentSize, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(102, 126, 234, ${opacity})`;
        ctx.fill();
        ctx.shadowBlur = 4;
        ctx.shadowColor = 'rgba(102, 126, 234, 0.6)';
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    }

    const dataPoints = [];
    for (let i = 0; i < 12; i++) {
      dataPoints.push(new DataPoint());
    }

    const handleMouseMove = (e) => {
      if (!isHovered) return;
      const rect = canvas.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    };

    const handleMouseEnter = () => {
      setIsHovered(true);
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      setMousePos({ x: 0, y: 0 });
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseenter', handleMouseEnter);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      time += 0.02;
      morphPhase += 0.01;

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // Calculate mouse influence
      const mouseDistance = isHovered ? Math.sqrt(
        Math.pow(mousePos.x - centerX, 2) + 
        Math.pow(mousePos.y - centerY, 2)
      ) : Infinity;
      const mouseInfluence = isHovered ? Math.max(0, 1 - mouseDistance / 60) : 0;
      const glowIntensity = 0.3 + mouseInfluence * 0.7;

      // Draw shield with morphing effect
      ctx.save();
      ctx.translate(centerX, centerY);
      
      // Shield shape with morphing
      const shieldPoints = 8;
      const baseRadius = 30;
      const morphAmount = Math.sin(morphPhase) * 5;
      
      ctx.beginPath();
      for (let i = 0; i < shieldPoints; i++) {
        const angle = (i / shieldPoints) * Math.PI * 2 - Math.PI / 2;
        const radius = baseRadius + Math.sin(morphPhase + i) * morphAmount;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.closePath();

      // Shield gradient
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, baseRadius);
      gradient.addColorStop(0, `rgba(102, 126, 234, ${0.4 * glowIntensity})`);
      gradient.addColorStop(0.5, `rgba(118, 75, 162, ${0.3 * glowIntensity})`);
      gradient.addColorStop(1, `rgba(102, 126, 234, ${0.15 * glowIntensity})`);
      
      ctx.fillStyle = gradient;
      ctx.fill();

      // Shield border with glow
      ctx.strokeStyle = `rgba(102, 126, 234, ${0.8 * glowIntensity})`;
      ctx.lineWidth = 2;
      ctx.shadowBlur = 12 * glowIntensity;
      ctx.shadowColor = 'rgba(102, 126, 234, 1)';
      ctx.stroke();
      ctx.shadowBlur = 0;

      ctx.restore();

      // Draw data points
      dataPoints.forEach(point => {
        point.update();
        point.draw(centerX, centerY);
      });

      // Draw glowing R
      ctx.save();
      ctx.translate(centerX, centerY);
      
      // R letter with glow effect - draw multiple layers for better glow
      const fontSize = 24 + Math.sin(time) * 2;
      ctx.font = `bold ${fontSize}px Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      const rGlow = glowIntensity * 15;
      
      // Draw multiple glow layers
      for (let i = 4; i >= 0; i--) {
        ctx.shadowBlur = rGlow - i * 2;
        ctx.shadowColor = `rgba(102, 126, 234, ${0.8 - i * 0.15})`;
        ctx.fillStyle = `rgba(255, 255, 255, ${0.2 + (4 - i) * 0.15})`;
        ctx.fillText('R', 0, 0);
      }
      
      // Final R letter on top
      ctx.shadowBlur = 0;
      ctx.fillStyle = `rgba(255, 255, 255, ${0.9 + mouseInfluence * 0.1})`;
      ctx.fillText('R', 0, 0);

      ctx.restore();

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseenter', handleMouseEnter);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      cancelAnimationFrame(animationFrameId);
    };
  }, [mousePos, isHovered]);

  return (
    <div 
      ref={containerRef} 
      className="logo-shield-container"
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <canvas ref={canvasRef} className="logo-shield-canvas" />
    </div>
  );
};

export default LogoShield;

